// src/hooks/queries/useArchivos.ts
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { archivosService } from '@/services/archivos';
import { toast } from '@/components/ui';
import type { ArchivoContexto, ArchivoListResponse } from '@/types';

export function useArchivos(contexto: ArchivoContexto, entidadId: string | undefined) {
  return useQuery<ArchivoListResponse>({
    queryKey: ['archivos', contexto, entidadId],
    queryFn: () => archivosService.listar(contexto, entidadId!),
    enabled: !!entidadId,
  });
}

export function useSubirMultiplesArchivos(contexto: ArchivoContexto) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ entidadId, files }: { entidadId: string; files: File[] }) =>
      archivosService.subirMultiples(contexto, entidadId, files),
    onSuccess: (result, { entidadId }) => {
      queryClient.invalidateQueries({ queryKey: ['archivos', contexto, entidadId] });
      if (result.total_fallidos > 0) {
        toast.warning(`${result.total_subidos} subidos, ${result.total_fallidos} fallidos`);
      } else {
        toast.success(`${result.total_subidos} archivo(s) subido(s)`);
      }
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Error al subir archivos');
    },
  });
}

export function useEliminarArchivo(contexto: ArchivoContexto) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ archivoId }: { archivoId: string; entidadId: string }) =>
      archivosService.eliminar(archivoId),
    onSuccess: (_, { entidadId }) => {
      queryClient.invalidateQueries({ queryKey: ['archivos', contexto, entidadId] });
      toast.success('Archivo eliminado');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Error al eliminar archivo');
    },
  });
}
